import { useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  format,
  parseISO,
  isWithinInterval,
  differenceInCalendarDays,
} from "date-fns";
import { CalendarEvent, colorHex } from "./types";

interface Props {
  monthDate: Date;
  events: CalendarEvent[];
  onDayClick: (date: Date) => void;
  onEventClick: (ev: CalendarEvent) => void;
  onShowMore: (date: Date) => void;
}

const WEEK_DAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MAX_LANES = 3; // visible bars per cell — above this we show "+N mais"

interface PositionedBar {
  event: CalendarEvent;
  startCol: number; // 0..6
  span: number; // 1..7
  lane: number; // 0..n
  isStart: boolean;
  isEnd: boolean;
}

function buildWeekRows(monthDate: Date): Date[][] {
  const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 });
  const days: Date[] = [];
  let d = gridStart;
  while (d <= gridEnd) {
    days.push(d);
    d = addDays(d, 1);
  }
  const rows: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
  return rows;
}

function layoutWeek(week: Date[], events: CalendarEvent[]): PositionedBar[] {
  const weekStart = week[0];
  const weekEnd = week[6];
  // Filter events overlapping this week
  const relevant = events
    .map((e) => ({
      e,
      s: parseISO(e.startDate),
      eDate: parseISO(e.endDate),
    }))
    .filter(({ s, eDate }) => eDate >= weekStart && s <= weekEnd)
    // Sort: earlier start first, then longer duration first
    .sort((a, b) => {
      const d = a.s.getTime() - b.s.getTime();
      if (d !== 0) return d;
      return (b.eDate.getTime() - b.s.getTime()) - (a.eDate.getTime() - a.s.getTime());
    });

  // Lane allocation: lanes[laneIdx] = last occupied col (0..6) or -1
  const lanes: number[] = [];
  const bars: PositionedBar[] = [];

  for (const { e, s, eDate } of relevant) {
    const segStart = s < weekStart ? weekStart : s;
    const segEnd = eDate > weekEnd ? weekEnd : eDate;
    const startCol = differenceInCalendarDays(segStart, weekStart);
    const endCol = differenceInCalendarDays(segEnd, weekStart);
    const span = endCol - startCol + 1;
    // find first lane where lanes[i] < startCol
    let lane = lanes.findIndex((last) => last < startCol);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(endCol);
    } else {
      lanes[lane] = endCol;
    }
    bars.push({
      event: e,
      startCol,
      span,
      lane,
      isStart: isSameDay(segStart, s),
      isEnd: isSameDay(segEnd, eDate),
    });
  }
  return bars;
}

export function CalendarGrid({ monthDate, events, onDayClick, onEventClick, onShowMore }: Props) {
  const weeks = useMemo(() => buildWeekRows(monthDate), [monthDate]);
  const today = new Date();

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
      {/* Header weekdays */}
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {WEEK_DAYS.map((d) => (
          <div
            key={d}
            className="px-2 py-2 text-[11px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Weeks */}
      <div className="divide-y divide-border">
        {weeks.map((week, wi) => {
          const bars = layoutWeek(week, events);
          // group bars by lane occupancy per day to compute "+N mais"
          const hiddenCountByDay: number[] = [0, 0, 0, 0, 0, 0, 0];
          bars.forEach((b) => {
            if (b.lane >= MAX_LANES) {
              for (let c = b.startCol; c < b.startCol + b.span; c++) hiddenCountByDay[c]++;
            }
          });
          const visibleBars = bars.filter((b) => b.lane < MAX_LANES);

          return (
            <div key={wi} className="relative grid grid-cols-7 min-h-[96px] sm:min-h-[120px]">
              {week.map((day, di) => {
                const isCurrentMonth = isSameMonth(day, monthDate);
                const isToday = isSameDay(day, today);
                return (
                  <div
                    key={di}
                    onClick={() => onDayClick(day)}
                    className={`relative border-r border-border last:border-r-0 p-1 sm:p-1.5 cursor-pointer transition-colors hover:bg-muted/40 ${
                      isCurrentMonth ? "" : "bg-muted/20"
                    }`}
                  >
                    <div className="flex items-center justify-end">
                      <span
                        className={`inline-flex items-center justify-center text-[11px] sm:text-xs font-semibold ${
                          isToday
                            ? "h-6 w-6 rounded-full bg-gradient-to-br from-[hsl(225_85%_56%)] to-[hsl(228_80%_48%)] text-white shadow-sm"
                            : isCurrentMonth
                              ? "text-foreground h-6 w-6"
                              : "text-muted-foreground/60 h-6 w-6"
                        }`}
                      >
                        {format(day, "d")}
                      </span>
                    </div>
                    {hiddenCountByDay[di] > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onShowMore(day);
                        }}
                        className="absolute bottom-1 left-1 right-1 text-[10px] sm:text-[11px] font-medium text-primary hover:underline text-left px-1"
                      >
                        +{hiddenCountByDay[di]} mais
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Event bars overlay */}
              <div className="pointer-events-none absolute inset-x-0 top-7 sm:top-8 bottom-1">
                {visibleBars.map((b, idx) => {
                  const left = (b.startCol / 7) * 100;
                  const width = (b.span / 7) * 100;
                  const top = b.lane * 22;
                  const hex = colorHex(b.event.color);
                  return (
                    <button
                      key={`${b.event.id}-${idx}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(b.event);
                      }}
                      className="pointer-events-auto absolute h-[19px] px-1.5 text-[10px] sm:text-[11px] font-medium text-white truncate flex items-center hover:brightness-110 transition-all shadow-sm"
                      style={{
                        left: `calc(${left}% + 2px)`,
                        width: `calc(${width}% - 4px)`,
                        top: `${top}px`,
                        backgroundColor: hex,
                        borderTopLeftRadius: b.isStart ? 6 : 2,
                        borderBottomLeftRadius: b.isStart ? 6 : 2,
                        borderTopRightRadius: b.isEnd ? 6 : 2,
                        borderBottomRightRadius: b.isEnd ? 6 : 2,
                      }}
                      title={b.event.title}
                    >
                      <span className="truncate">
                        {b.isStart ? b.event.title : `↳ ${b.event.title}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Helper used by parent to list all events occurring on a specific date. */
export function eventsOnDate(events: CalendarEvent[], date: Date): CalendarEvent[] {
  return events.filter((e) => {
    const s = parseISO(e.startDate);
    const en = parseISO(e.endDate);
    return isWithinInterval(date, { start: s, end: en });
  });
}